(function () {
  const rootSelector = '[data-feedback-drawer]';
  const submitTimeout = 12000;

  function init() {
    document.querySelectorAll(rootSelector).forEach((root) => {
      if (root.dataset.feedbackInitialized === 'true') return;
      root.dataset.feedbackInitialized = 'true';
      new FeedbackDrawer(root);
    });
  }

  class FeedbackDrawer {
    constructor(root) {
      this.root = root;
      this.trigger = root.querySelector('[data-feedback-open]');
      this.overlay = root.querySelector('[data-feedback-close]');
      this.dialog = root.querySelector('[data-feedback-dialog]');
      this.form = root.querySelector('[data-feedback-form]');
      this.submitButton = root.querySelector('[data-feedback-submit]');
      this.submitLabel = root.querySelector('[data-feedback-submit-label]');
      this.error = root.querySelector('[data-feedback-error]');
      this.status = root.querySelector('[data-feedback-status]');
      this.success = root.querySelector('[data-feedback-success]');
      this.frame = root.querySelector('iframe');
      this.pageContext = root.dataset.pageContext || window.location.pathname;
      this.lastFocus = null;
      this.offerContext = this.getRouteOfferContext() || this.restoreOfferContext();
      this.pending = false;
      this.pendingTimeout = null;
      this.closeTimeout = null;
      this.endpointMessageHandler = (event) => this.handleEndpointMessage(event);
      this.bind();
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-feedback-open]');
        if (openButton) {
          this.open();
          return;
        }

        const closeButton = event.target.closest('[data-feedback-close]');
        if (closeButton) this.close();
      });

      this.form.addEventListener('submit', (event) => this.submit(event));
      this.frame.addEventListener('load', () => this.handleFrameLoad());
      window.addEventListener('message', this.endpointMessageHandler);

      this.root.querySelectorAll('[data-offer-url]').forEach((card) => {
        const link = card.querySelector('a');
        if (!link) return;
        link.addEventListener('click', () => this.saveOfferContext(card));
      });

      document.addEventListener('keydown', (event) => {
        if (this.dialog.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    open() {
      if (!this.dialog.hidden) return;
      this.lastFocus = document.activeElement;
      window.clearTimeout(this.closeTimeout);
      this.dialog.hidden = false;
      this.overlay.hidden = false;
      this.dialog.setAttribute('aria-hidden', 'false');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('dt-feedback-drawer-open');
      window.requestAnimationFrame(() => {
        this.root.classList.add('is-open');
        const firstFocusable = this.getFocusable()[0];
        if (firstFocusable) firstFocusable.focus();
      });
    }

    close() {
      if (this.dialog.hidden) return;
      this.root.classList.remove('is-open');
      this.dialog.setAttribute('aria-hidden', 'true');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('dt-feedback-drawer-open');
      window.clearTimeout(this.closeTimeout);
      this.closeTimeout = window.setTimeout(() => {
        if (!this.root.classList.contains('is-open')) {
          this.dialog.hidden = true;
          this.overlay.hidden = true;
          this.resetView();
        }
      }, 240);

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
    }

    submit(event) {
      event.preventDefault();
      if (this.pending) return;

      const reasons = Array.from(this.root.querySelectorAll('[data-feedback-reason]:checked'));
      const detailsField = this.root.querySelector('[data-feedback-details]');
      const details = detailsField.value.trim();

      this.clearError();
      if (!reasons.length && !details) {
        this.showError('Choose one reason or add a note so we know what to improve.');
        const firstReason = this.root.querySelector('[data-feedback-reason]');
        if (firstReason) firstReason.focus();
        return;
      }

      const config = this.getConfig();
      if (config.endpoint) {
        this.submitToEndpoint(config, reasons.map((reason) => reason.value), details);
        return;
      }

      if (!config.action || !config.page || !config.offer || !config.reasons || !config.details) {
        this.showError('Add the response URL and field IDs in Theme settings → Bundle feedback.');
        return;
      }

      this.form.action = config.action;
      this.clearGeneratedFields();
      this.addHiddenField(config.page, this.pageContext);
      this.addHiddenField(config.offer, this.offerContext || 'No offer selected');
      reasons.forEach((reason) => this.addHiddenField(config.reasons, reason.value));
      this.addHiddenField(config.details, details);

      this.beginSubmit();
      HTMLFormElement.prototype.submit.call(this.form);
    }

    submitToEndpoint(config, reasons, details) {
      this.beginSubmit();

      this.form.action = config.endpoint;
      this.form.target = this.frame.name;
      this.clearGeneratedFields();
      this.addHiddenField('page', this.pageContext);
      this.addHiddenField('offer', this.offerContext || 'No offer selected');
      this.addHiddenField('reasons', reasons.join(', '));
      this.addHiddenField('details', details);
      HTMLFormElement.prototype.submit.call(this.form);
    }

    handleEndpointMessage(event) {
      if (!this.pending) return;
      if (!event.data || event.data.type !== 'dt-feedback-response') return;
      if (event.data.success) {
        this.showSuccess();
        return;
      }

      this.endSubmit();
      this.restoreFormAfterFailure();
      this.showError('We could not save your feedback. Please try once more.');
    }

    showSuccess() {
      this.endSubmit();
      delete this.success.dataset.optimistic;
      this.status.textContent = '';
      this.clearError();
      this.form.hidden = true;
      this.success.hidden = false;
      this.success.focus();
    }

    handleFrameLoad() {
      if (!this.pending) return;
      this.endSubmit();
      delete this.success.dataset.optimistic;
      this.status.textContent = '';
      this.clearError();
      this.form.hidden = true;
      this.success.hidden = false;
      this.success.focus();
    }

    getConfig() {
      return {
        endpoint: String(this.root.dataset.feedbackEndpoint || '').trim(),
        action: this.root.dataset.formAction.trim(),
        page: this.entryName(this.root.dataset.pageField),
        offer: this.entryName(this.root.dataset.offerField),
        reasons: this.entryName(this.root.dataset.reasonsField),
        details: this.entryName(this.root.dataset.detailsField)
      };
    }

    entryName(value) {
      const id = String(value || '').trim();
      return /^\d+$/.test(id) ? `entry.${id}` : '';
    }

    addHiddenField(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.dataset.feedbackGenerated = 'true';
      this.form.appendChild(input);
    }

    clearGeneratedFields() {
      this.form.querySelectorAll('[data-feedback-generated]').forEach((field) => field.remove());
    }

    getFocusable() {
      return Array.from(this.dialog.querySelectorAll('button, input, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.disabled && !element.hidden && element.offsetParent !== null);
    }

    trapFocus(event) {
      const focusable = this.getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    saveOfferContext(card) {
      const title = card.dataset.offerTitle || 'Latest Offers';
      const url = card.dataset.offerUrl || '';
      this.offerContext = `${title}${url ? ` (${url})` : ''}`;
      try {
        sessionStorage.setItem('dtLatestOffersFeedbackOffer', this.offerContext);
      } catch (_) {}
    }

    restoreOfferContext() {
      try {
        return sessionStorage.getItem('dtLatestOffersFeedbackOffer') || '';
      } catch (_) {
        return '';
      }
    }

    getRouteOfferContext() {
      const path = String(window.location.pathname || '').replace(/\/$/, '');
      const nativeMatch = path.match(/\/pages\/(?:buy-any-3-at-|bundle-)(\d+)$/);
      if (nativeMatch) return `buy-3-at-${nativeMatch[1]}`;

      const easyBundleMatch = path.match(/\/apps\/gbb\/easybundle\/(buy-3-at-\d+)$/);
      return easyBundleMatch ? easyBundleMatch[1] : '';
    }

    resetView() {
      if (this.pending) return;
      delete this.success.dataset.optimistic;
      this.form.hidden = false;
      this.success.hidden = true;
      this.form.reset();
      this.clearGeneratedFields();
      this.clearError();
      this.status.textContent = '';
      this.endSubmit();
    }

    beginSubmit() {
      this.pending = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      if (this.submitLabel) this.submitLabel.textContent = 'Sending…';
      this.status.textContent = 'Saving your feedback…';
      this.form.hidden = true;
      this.success.hidden = false;
      this.success.dataset.optimistic = 'true';
      this.success.focus();
      window.clearTimeout(this.pendingTimeout);
      this.pendingTimeout = window.setTimeout(() => {
        if (!this.pending) return;
        this.endSubmit();
        this.restoreFormAfterFailure();
        this.status.textContent = '';
        this.showError('We could not confirm the response. Please try once more.');
      }, submitTimeout);
    }

    endSubmit() {
      this.pending = false;
      window.clearTimeout(this.pendingTimeout);
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
      if (this.submitLabel) this.submitLabel.textContent = 'Send feedback';
    }

    restoreFormAfterFailure() {
      delete this.success.dataset.optimistic;
      this.success.hidden = true;
      this.form.hidden = false;
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      this.error.textContent = '';
      this.error.hidden = true;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
